import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { CaretLeft, CaretRight, CalendarCheck } from "@phosphor-icons/react";

const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const DIAS = ["D", "L", "M", "M", "J", "V", "S"];
const pad = (n) => String(n).padStart(2, "0");

// Grilla cajas × días del mes: verde = extracto importado ese día.
export default function ImportCalendar({ refreshKey }) {
  const hoy = new Date();
  const [ym, setYm] = useState({ y: hoy.getFullYear(), m: hoy.getMonth() });
  const [data, setData] = useState(null);
  const mes = `${ym.y}-${pad(ym.m + 1)}`;
  const diasMes = new Date(ym.y, ym.m + 1, 0).getDate();
  const hoyISO = `${hoy.getFullYear()}-${pad(hoy.getMonth() + 1)}-${pad(hoy.getDate())}`;

  useEffect(() => {
    api.get("/import-batches/calendario", { params: { mes } }).then(({ data }) => setData(data)).catch(() => setData({ cajas: [] }));
  }, [mes, refreshKey]);

  const cajas = useMemo(() => (data?.cajas || []).map((c) => ({ ...c, set: new Set(c.fechas) })), [data]);
  const mover = (d) => setYm(({ y, m }) => { const dt = new Date(y, m + d, 1); return { y: dt.getFullYear(), m: dt.getMonth() }; });

  return (
    <div data-testid="import-calendar" className="bg-white border border-[#E5E5E5] rounded-sm">
      <div className="px-5 py-3 border-b border-[#E5E5E5] flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <CalendarCheck size={16} weight="bold" className="text-[#6B1F24]" />
          <h3 className="font-heading text-[14px] font-semibold">Extractos importados</h3>
          <span className="text-[11px] text-neutral-500 hidden md:inline">· verde = ya se subió el extracto de ese día; si lo volvés a importar, esas fechas se omiten</span>
        </div>
        <div className="flex items-center gap-1">
          <button data-testid="import-calendar-prev" onClick={() => mover(-1)} className="p-1.5 rounded-sm hover:bg-neutral-100"><CaretLeft size={14} /></button>
          <span data-testid="import-calendar-mes" className="font-mono text-[12px] w-36 text-center">{MESES[ym.m]} {ym.y}</span>
          <button data-testid="import-calendar-next" onClick={() => mover(1)} className="p-1.5 rounded-sm hover:bg-neutral-100"><CaretRight size={14} /></button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="text-[11px] border-collapse">
          <thead>
            <tr>
              <th className="sticky left-0 bg-white text-left px-4 py-1.5 font-mono text-[10px] uppercase text-[#666] min-w-[160px]">Caja</th>
              {Array.from({ length: diasMes }, (_, i) => {
                const d = new Date(ym.y, ym.m, i + 1);
                const finde = d.getDay() === 0 || d.getDay() === 6;
                return (
                  <th key={i} className={`px-0 py-1 font-mono font-normal text-[10px] w-7 min-w-[28px] text-center ${finde ? "text-neutral-300" : "text-[#666]"}`}>
                    <div>{DIAS[d.getDay()]}</div><div>{i + 1}</div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {cajas.length === 0 && (
              <tr><td colSpan={diasMes + 1} className="px-4 py-5 text-center text-neutral-400">No hay cajas de tipo banco o digital.</td></tr>
            )}
            {cajas.map((c) => (
              <tr key={c.id} data-testid={`import-calendar-row-${c.id}`} className="border-t border-[#F0F0F0]">
                <td className="sticky left-0 bg-white px-4 py-1.5 whitespace-nowrap">
                  <span className="font-medium">{c.nombre}</span>
                  <span className="ml-2 font-mono text-[10px] text-emerald-700">{c.fechas.length}/{diasMes}</span>
                </td>
                {Array.from({ length: diasMes }, (_, i) => {
                  const iso = `${mes}-${pad(i + 1)}`;
                  const ok = c.set.has(iso);
                  const futuro = iso > hoyISO;
                  const d = new Date(ym.y, ym.m, i + 1);
                  const finde = d.getDay() === 0 || d.getDay() === 6;
                  return (
                    <td key={i} className="p-0.5">
                      <div
                        data-testid={ok ? `import-ok-${c.id}-${iso}` : undefined}
                        title={`${c.nombre} · ${pad(i + 1)}/${pad(ym.m + 1)}/${ym.y}${ok ? " · importado" : ""}`}
                        className={`h-6 w-6 rounded-sm transition-colors duration-150 ${ok ? "bg-emerald-500 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.08)]" : futuro ? "bg-transparent" : finde ? "bg-neutral-100" : "bg-neutral-200/80"}`}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
